<template>
  <div class="create-group-page">
    <!-- Header -->
    <div class="page-header">
      <button class="back-btn" @click="goBack">
        <i class="bi bi-arrow-left"></i>
      </button>
      <h1>{{ $t("chat.create_group_chat") || "Create Group Chat" }}</h1>
      <div class="header-spacer"></div>
    </div>

    <!-- Content -->
    <div class="page-content">
      <!-- Group Info Section -->
      <div class="section group-info-section">
        <!-- Group Photo -->
        <div class="group-photo-picker" :class="{ 'has-photo': photoPreview }">
          <div class="photo-preview" @click="triggerPhotoPicker">
            <img v-if="photoPreview" :src="photoPreview" alt="Group photo" />
            <div v-else class="photo-placeholder">
              <i class="bi bi-camera-fill"></i>
              <span class="upload-hint">{{ $t('chat.tap_to_upload') || 'Tap to upload' }}</span>
            </div>
            <div class="photo-overlay" :class="{ 'edit-mode': photoPreview }">
              <i :class="photoPreview ? 'bi bi-pencil-fill' : 'bi bi-plus-lg'"></i>
            </div>
            <div v-if="!photoPreview" class="pulse-ring"></div>
          </div>
          <input
            ref="photoInput"
            type="file"
            accept="image/jpeg,image/png,image/gif"
            @change="onPhotoSelected"
            style="display: none"
          />
          <div class="photo-info">
            <span class="photo-label">{{
              $t("chat.group_photo") || "Group Photo"
            }}</span>
            <span class="photo-hint">{{
              $t("chat.optional") || "Optional"
            }}</span>
          </div>
        </div>

        <!-- Group Name -->
        <div class="input-group">
          <label>{{ $t("chat.group_name") || "Group Name" }} *</label>
          <input
            type="text"
            v-model="groupName"
            :placeholder="$t('chat.enter_group_name') || 'Enter group name'"
            maxlength="100"
            :disabled="creating"
          />
          <span class="char-count">{{ groupName.length }}/100</span>
        </div>
      </div>

      <!-- Participants Section -->
      <div class="section participants-section">
        <div class="section-header">
          <h2>{{ $t("chat.select_participants") || "Select Participants" }}</h2>
          <span
            class="participant-count"
            :class="{ warning: selectedCount < 2, valid: selectedCount >= 2 }"
          >
            {{ selectedCount }} / 49
          </span>
        </div>
        <p class="section-hint">
          {{
            $t("chat.min_2_max_49_participants") ||
            "Minimum 2, maximum 49 participants"
          }}
        </p>

        <!-- Loading Conversations -->
        <div v-if="loadingConversations" class="loading-state">
          <div class="spinner"></div>
          <span>{{ $t("common.loading") || "Loading..." }}</span>
        </div>

        <!-- Empty State -->
        <div v-else-if="validConversations.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="bi bi-people"></i>
          </div>
          <h3>
            {{
              $t("chat.no_conversations_to_add") || "No conversations to add"
            }}
          </h3>
          <p>
            {{
              $t("chat.accept_requests_first") ||
              "Accept message requests to add participants"
            }}
          </p>
        </div>

        <!-- Participants List -->
        <div v-else class="participants-list">
          <div
            v-for="conv in validConversations"
            :key="conv.id"
            class="participant-item"
            :class="{ selected: isSelected(conv) }"
            @click="toggleParticipant(conv)"
          >
            <!-- Checkbox -->
            <div class="checkbox">
              <i v-if="isSelected(conv)" class="bi bi-check-lg"></i>
            </div>

            <!-- Avatar -->
            <div class="participant-avatar">
              <img
                v-if="getOtherUser(conv)?.photoUrl"
                :src="getOtherUser(conv).photoUrl"
                :alt="getOtherUser(conv)?.name"
              />
              <div v-else class="avatar-placeholder">
                {{ getInitials(getOtherUser(conv)?.name) }}
              </div>
            </div>

            <!-- Info -->
            <div class="participant-info">
              <h4>{{ getOtherUser(conv)?.name || "Unknown" }}</h4>
              <span class="last-message" v-if="conv.lastMessage">
                {{
                  truncateMessage(
                    conv.lastMessage.text || conv.lastMessage.preview
                  )
                }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="error-message">
        <i class="bi bi-exclamation-circle"></i>
        {{ error }}
      </div>
    </div>

    <!-- Footer -->
    <div class="page-footer">
      <button
        class="create-btn"
        @click="createGroup"
        :disabled="!canCreate || creating"
      >
        <i v-if="creating" class="bi bi-hourglass-split spin"></i>
        <i v-else class="bi bi-people-fill"></i>
        {{
          creating
            ? $t("chat.creating") || "Creating..."
            : $t("chat.create_group") || "Create Group"
        }}
      </button>
    </div>
  </div>
</template>

<script>
import { ChatService } from "@/services/chat";
import { chatState } from "@/stores/chat";

export default {
  name: "CreateGroupChat",
  data() {
    return {
      groupName: "",
      photo: null,
      photoPreview: null,
      selectedParticipants: [],
      conversations: [],
      loadingConversations: true,
      creating: false,
      error: null,
    };
  },
  computed: {
    selectedCount() {
      return this.selectedParticipants.length;
    },
    canCreate() {
      return (
        this.groupName.trim().length > 0 &&
        this.selectedCount >= 2 &&
        this.selectedCount <= 49
      );
    },
    // Filter conversations to only show those with valid other users
    validConversations() {
      // Wait for userId to be available
      if (!chatState.userId) {
        console.log("[CreateGroupChat] Waiting for userId...");
        return [];
      }

      const currentUserId = String(chatState.userId);

      return this.conversations.filter((conv) => {
        // Use parsedParticipants (from custom.participants_details)
        if (conv.parsedParticipants && conv.parsedParticipants.length > 0) {
          const otherUser = conv.parsedParticipants.find(
            (p) => String(p.id) !== currentUserId
          );
          return otherUser && otherUser.id;
        }

        // Fallback: check participantIds
        if (conv.participantIds && conv.participantIds.length > 0) {
          const otherUserId = conv.participantIds.find(
            (id) => String(id) !== currentUserId
          );
          return !!otherUserId;
        }

        return false;
      });
    },
  },
  mounted() {
    this.loadConversations();
  },
  methods: {
    goBack() {
      this.$router.back();
    },

    async loadConversations() {
      this.loadingConversations = true;
      this.error = null;

      try {
        const allConversations = await ChatService.getDirectConversations();
        console.log(
          "[CreateGroupChat] Loaded conversations:",
          allConversations
        );
        // Direct conversations are already filtered by the service
        this.conversations = allConversations;
      } catch (e) {
        console.error("[CreateGroupChat] Failed to load conversations:", e);
        this.error = e?.message || "Failed to load conversations";
      } finally {
        this.loadingConversations = false;
      }
    },

    triggerPhotoPicker() {
      this.$refs.photoInput?.click();
    },

    onPhotoSelected(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.error =
          this.$t("chat.photo_too_large") || "Photo must be less than 5MB";
        return;
      }

      this.photo = file;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.photoPreview = e.target?.result;
      };
      reader.readAsDataURL(file);
    },

    getOtherUser(conversation) {
      // Get current user ID
      const currentUserId = String(chatState.userId);
      if (!currentUserId) {
        console.warn("[CreateGroupChat] No current user ID available");
        return null;
      }

      // Use parsedParticipants from the service (parsed from custom.participants_details)
      if (
        conversation.parsedParticipants &&
        conversation.parsedParticipants.length > 0
      ) {
        const otherUser = conversation.parsedParticipants.find(
          (p) => String(p.id) !== currentUserId
        );
        if (otherUser) {
          return {
            id: otherUser.id,
            name: otherUser.name,
            photoUrl: otherUser.image || conversation.photoUrl,
          };
        }
      }

      // Fallback: use participantIds and subject/photoUrl
      if (
        conversation.participantIds &&
        conversation.participantIds.length > 0
      ) {
        const otherUserId = conversation.participantIds.find(
          (id) => String(id) !== currentUserId
        );
        if (otherUserId) {
          return {
            id: otherUserId,
            name: conversation.subject || "User",
            photoUrl: conversation.photoUrl,
          };
        }
      }

      return null;
    },

    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },

    truncateMessage(text) {
      if (!text) return "";
      return text.length > 40 ? text.substring(0, 40) + "..." : text;
    },

    isSelected(conversation) {
      const otherUser = this.getOtherUser(conversation);
      if (!otherUser) return false;
      // Compare as strings to handle both string and number IDs
      return this.selectedParticipants.some(
        (id) => String(id) === String(otherUser.id)
      );
    },

    toggleParticipant(conversation) {
      const otherUser = this.getOtherUser(conversation);
      if (!otherUser) {
        console.warn("[CreateGroupChat] No other user found for conversation");
        return;
      }

      const oderId = String(otherUser.id);
      const index = this.selectedParticipants.findIndex(
        (id) => String(id) === oderId
      );

      if (index > -1) {
        this.selectedParticipants.splice(index, 1);
      } else {
        if (this.selectedCount >= 49) {
          this.error =
            this.$t("chat.max_49_participants") || "Maximum 49 participants";
          return;
        }
        // Store as string, will convert to int when sending to API
        this.selectedParticipants.push(otherUser.id);
      }
      this.error = null;
    },

    async createGroup() {
      if (!this.canCreate) return;

      // Validation
      if (this.groupName.trim().length === 0) {
        this.error =
          this.$t("chat.group_name_required") || "Group name is required";
        return;
      }

      if (this.selectedCount < 2) {
        this.error =
          this.$t("chat.select_at_least_2_participants") ||
          "Select at least 2 participants";
        return;
      }

      this.creating = true;
      this.error = null;

      try {
        const result = await ChatService.createGroupChat({
          name: this.groupName.trim(),
          participantIds: this.selectedParticipants,
          photo: this.photo,
        });

        if (!result.success) {
          this.error =
            result.message ||
            this.$t("chat.failed_to_create_group") ||
            "Failed to create group";
          return;
        }

        // Navigate to the new group conversation
        if (result.data?.conversation_id) {
          this.$router.push({
            name: "ChatConversation",
            params: { conversationId: result.data.conversation_id },
          });
        } else {
          this.$router.push({ name: "ChatInbox" });
        }
      } catch (e) {
        this.error =
          e?.message ||
          this.$t("chat.failed_to_create_group") ||
          "Failed to create group";
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<style scoped>
.create-group-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf4 100%);
  display: flex;
  flex-direction: column;
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
}

.back-btn {
  width: 44px;
  height: 44px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #ffffff !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  transition: background 0.2s;
}

.back-btn i {
  color: #ffffff !important;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.page-header h1 {
  flex: 1;
  margin: 0;
  font-size: 1.35rem;
  font-weight: 600;
}

.header-spacer {
  width: 44px;
}

/* Content */
.page-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.section {
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* Group Info Section */
.group-photo-picker {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.photo-preview {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: visible;
  cursor: pointer;
  background: #f1f5f9;
  border: 3px dashed #00c444;
  transition: all 0.3s ease;
}

.has-photo .photo-preview {
  border: 3px solid #00c444;
  border-style: solid;
}

.photo-preview:hover {
  transform: scale(1.05);
  border-color: #00a838;
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.25);
}

.photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8fff1 0%, #d1fae5 100%);
  color: #00a838;
  font-size: 2rem;
  border-radius: 50%;
  gap: 4px;
}

.photo-placeholder i {
  font-size: 2.2rem;
  animation: bounce 2s ease-in-out infinite;
}

.upload-hint {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #00a838;
  opacity: 0.9;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

.pulse-ring {
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border: 2px solid #00c444;
  border-radius: 50%;
  animation: pulse 2s ease-out infinite;
  pointer-events: none;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.15);
    opacity: 0;
  }
}

.photo-overlay {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.9rem;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.4);
  transition: all 0.3s ease;
  z-index: 2;
}

.photo-overlay:not(.edit-mode) {
  animation: pulse-overlay 1.5s ease-in-out infinite;
}

@keyframes pulse-overlay {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.photo-preview:hover .photo-overlay {
  transform: scale(1.15);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.5);
}

.photo-info {
  display: flex;
  flex-direction: column;
}

.photo-label {
  font-weight: 500;
  color: #1a1a2e;
}

.photo-hint {
  font-size: 0.8rem;
  color: #94a3b8;
}

.input-group {
  position: relative;
}

.input-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.input-group input {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: border-color 0.2s;
}

.input-group input:focus {
  outline: none;
  border-color: #00c444;
}

.char-count {
  position: absolute;
  right: 12px;
  bottom: 14px;
  font-size: 0.75rem;
  color: #94a3b8;
}

/* Participants Section */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.section-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a2e;
}

.participant-count {
  font-size: 0.9rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
}

.participant-count.warning {
  background: #fef3c7;
  color: #d97706;
}

.participant-count.valid {
  background: #e8fff1;
  color: #00a838;
}

.section-hint {
  margin: 0 0 16px;
  font-size: 0.85rem;
  color: #64748b;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 20px;
  color: #64748b;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #00c444;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  background: #e8fff1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-icon i {
  font-size: 1.5rem;
  color: #00c444;
}

.empty-state h3 {
  margin: 0 0 8px;
  font-size: 1rem;
  color: #1a1a2e;
}

.empty-state p {
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
}

/* Participants List */
.participants-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.participant-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.participant-item:hover {
  background: #f8fafc;
}

.participant-item.selected {
  background: #e8fff1;
  border-color: #00c444;
}

.checkbox {
  width: 24px;
  height: 24px;
  border: 2px solid #cbd5e1;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.participant-item.selected .checkbox {
  background: #00c444;
  border-color: #00c444;
  color: white;
}

.participant-avatar {
  flex-shrink: 0;
}

.participant-avatar img {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
}

.participant-info {
  flex: 1;
  min-width: 0;
}

.participant-info h4 {
  margin: 0 0 2px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #1a1a2e;
}

.last-message {
  font-size: 0.8rem;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  background: #fef2f2;
  color: #dc2626;
  border-radius: 12px;
  font-size: 0.9rem;
}

/* Footer */
.page-footer {
  padding: 20px 24px;
  background: white;
  border-top: 1px solid #e2e8f0;
}

.create-btn {
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s;
}

.create-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.3);
}

.create-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spin {
  animation: spin 1s linear infinite;
}

/* RTL Support */
[dir="rtl"] .page-header {
  flex-direction: row-reverse;
}

[dir="rtl"] .back-btn i {
  transform: scaleX(-1);
}

[dir="rtl"] .group-photo-picker {
  flex-direction: row-reverse;
}

[dir="rtl"] .participant-item {
  flex-direction: row-reverse;
}

[dir="rtl"] .char-count {
  right: auto;
  left: 12px;
}

/* Responsive */
@media (max-width: 640px) {
  .page-content {
    padding: 16px;
  }

  .section {
    padding: 16px;
  }
}
</style>
